import { z } from 'zod';
import { LICENSE_CATEGORY_GROUP_KEYS } from '../constants/license-category';
import { MATCHMAKER_SOURCE_TYPES } from '../constants/license-source-type';
import { OrderStatus } from '../enums/order-status.enum';

/**
 * Schema for Access Logging settings
 */
export const AccessLoggingSchema = z.object({
	verboseLogging: z
		.boolean()
		.default(false)
		.describe('Enable verbose request/response logging'),
});

/**
 * Schema for the System AI Agent (Sales Helper) availability toggle.
 *
 * `enabled` controls whether the code-defined system agent (currently
 * `sales-helper`) is exposed in the CRM chat widget. Opt-in: defaults to
 * `false` so tenants must explicitly turn the widget on. The setting writer
 * also flips `is_chat_widget_default` on the matching `conversation_agent`
 * row so the widget endpoint can read DB state directly.
 */
export const SystemAgentEnabledSchema = z.object({
	enabled: z
		.boolean()
		.default(false)
		.describe(
			'Enable the system AI agent (Sales Helper) for the CRM chat widget. When disabled, the widget is hidden for all users in this tenant.',
		),
});

/**
 * Schema for AI Agent Credits settings
 */
export const ConversationAgentCreditsSchema = z.object({
	voiceMinuteCredits: z
		.number()
		.int('Must be a whole number')
		.min(0, 'Cannot be negative')
		.max(100_000_000, 'Maximum is 100 million minutes')
		.default(100)
		.describe('Monthly voice minute allocation'),

	llmTokenCredits: z
		.number()
		.int('Must be a whole number')
		.min(0, 'Cannot be negative')
		.max(100_000_000_000, 'Maximum is 100 billion tokens')
		.default(1_000_000)
		.describe('Monthly LLM token allocation'),
});

/**
 * Schema for RingCentral integration settings
 */
export const RingCentralSettingsSchema = z.object({
	callMode: z
		.enum(['ringout', 'callout'])
		.default('ringout')
		.describe(
			'Call mode: ringout (calls your phone first) or callout (browser-based)',
		),
});

/**
 * Schema for AI Agent Throttle Concurrency settings
 */
export const ThrottleConcurrencySchema = z.object({
	inbound: z
		.number()
		.int('Must be a whole number')
		.min(1, 'Minimum is 1')
		.max(100, 'Maximum is 100')
		.default(10)
		.describe('Max concurrent inbound voice and web sessions'),

	outbound: z
		.number()
		.int('Must be a whole number')
		.min(1, 'Minimum is 1')
		.max(100, 'Maximum is 100')
		.default(10)
		.describe('Max concurrent outbound voice calls'),

	chat: z
		.number()
		.int('Must be a whole number')
		.min(1, 'Minimum is 1')
		.max(100, 'Maximum is 100')
		.default(10)
		.describe('Max concurrent chat sessions'),
});

/**
 * Schema for the tenant-level lead registration notification recipient.
 * String form (single email) is the simple case; comma-separated list
 * works for multiple recipients without needing array gymnastics in the
 * admin UI. Empty string treated by callers as "not configured" so
 * unset tenants fall through to the system default / log warning path.
 */
export const LeadRegistrationEmailSchema = z
	.string()
	.default('')
	.describe(
		'Email recipient(s) for new lead registrations. Single address or comma-separated list. Empty = not configured.',
	);

/**
 * Schema for the tenant-level ERP integration mode.
 *
 * Two modes:
 *  - `workd_as_erp` (default) — Workd IS the ERP for this tenant. Orders
 *    that land in APPROVED status stay inside Workd, so the user can
 *    cancel them (and the trip-cancel flow can release / cancel them
 *    with auth-void).
 *  - `third_party` — Workd integrates with a downstream ERP (NetSuite,
 *    SAP, Acumatica, QuickBooks-Enterprise, etc.). Once an order is
 *    APPROVED it gets pushed out and we no longer control its
 *    lifecycle, so cancel-after-approval is rejected. (Note: as of
 *    2026-05-22 no actual third-party-ERP sync code exists in this
 *    codebase — the restriction has been theoretical. Tenants who add
 *    an ERP integration later flip this setting and wire their sync.)
 *
 * Cancel-order gate uses this setting at apps/crm-api/src/modules/
 * orders/orders.service.ts:cancelOrder — when `mode === 'workd_as_erp'`
 * the cancellable-status list includes APPROVED.
 */
export const ErpIntegrationSchema = z.object({
	mode: z
		.enum(['workd_as_erp', 'third_party'])
		.default('workd_as_erp')
		.describe(
			'workd_as_erp: orders remain inside Workd and stay cancellable after approval. third_party: orders sync to an external ERP and become non-cancellable post-approval.',
		),
});

/**
 * Schema for Pharmacy License Compliance settings.
 *
 * `enabled` controls whether the tenant uses pharmacy compliance features.
 * Auto-approval / auto-pairing has been removed: every registry match
 * lands as `pending_review` for explicit human confirmation on the
 * License Matchmaker screen. Old `autoApproveThreshold` and
 * `notifyOnAutoPair` keys may still appear in legacy instance_setting
 * rows but are no longer read.
 */
export const PharmacyComplianceSchema = z.object({
	enabled: z
		.boolean()
		.default(false)
		.describe(
			'Master switch for the licensing system. When OFF (default), every licensing background job (registry harvest, matchmaker scans, registry-status detection, mark-expired sweep, daily license notifications, compliance-stats refresh) is skipped for this tenant and order-validation skips license-status enforcement. Turn ON only after the tenant is set up to use pharmacy compliance (NPI, DEA verification, state board registry sync, enhanced order blocking).',
		),
	requireVerifiedLicenseAtCheckout: z
		.boolean()
		.default(false)
		.describe(
			'Customer-facing checkout enforcement (PD-14368). When OFF (default), the order-checkout license-good-standing surfaces are suppressed: the cart sidebar shows no license indicator and order submission is never blocked for a client lacking a confirmed/verified license — even while the rest of the licensing subsystem (matchmaker, dashboard, registry harvest) keeps running. Turn ON per-tenant only once matchmaking has been underway and licensing is being managed the new way, so we do not nag customers with negative-sentiment checkout warnings during ramp-up. Has effect only when `enabled` is also true.',
		),
	licenseCategories: z
		.array(z.string())
		.default(['PHARMACY'])
		.describe(
			'License categories to monitor (e.g. PHARMACY, PHARMACIST, TECHNICIAN)',
		),
	// PD-14842: per-tenant matchmaking toggles. Stored as DISABLED sets (default
	// empty = everything matched) so a newly-added source/category stays enabled
	// for existing tenants without a backfill. The matchmaker reads these to skip
	// source-specific passes and to filter the state registry slice.
	disabledMatchSourceTypes: z
		.array(z.enum(MATCHMAKER_SOURCE_TYPES))
		.default([])
		.meta({
			title: 'Disabled matchmaking sources',
			description:
				'Registry source types EXCLUDED from matchmaking for this tenant. Checked = excluded. Empty (default) = every source is matched. Example: check NPI to stop the matchmaker suggesting NPI records.',
		}),
	disabledMatchCategoryGroups: z
		.array(z.enum(LICENSE_CATEGORY_GROUP_KEYS))
		.default([])
		.meta({
			title: 'Disabled matchmaking categories',
			description:
				'License-category groups EXCLUDED from matchmaking for this tenant. Checked = excluded. Empty (default) = every category is matched. Example: check Veterinary to stop veterinary state-board matches while pharmacy state-board matching continues.',
		}),
	notifyOnExpiring: z
		.boolean()
		.default(false)
		.describe(
			'Send notifications when monitored licenses are nearing expiration. Defaults off — must be opted in per tenant.',
		),
	notifyDaysBeforeExpiry: z
		.number()
		.min(7)
		.max(180)
		.default(30)
		.describe(
			'Days before expiration to trigger an expiring-soon notification (7-180)',
		),
	notifyOnStatusChange: z
		.boolean()
		.default(false)
		.describe(
			'Send notifications when a license status changes (e.g. Active → Suspended). Defaults off — must be opted in per tenant.',
		),
	notifyOnDisciplinaryAction: z
		.boolean()
		.default(false)
		.describe(
			'Send notifications when a disciplinary action is detected on a license. Defaults off — must be opted in per tenant.',
		),
});

/**
 * Schema for the tenant-level "Pharmacy Sales" feature flag.
 *
 * Distinct from `PharmacyComplianceSchema` — that one is a per-tenant
 * licensing-subsystem switch (registry harvest, matchmaker, order
 * blocking). `PharmacySalesSchema.enabled` is the higher-level question
 * "does this tenant sell to pharmacy clients?" — when true the SPA shows
 * pharmacy-specific UI (NPI / NCPDP fields on client, pharmacy labeling,
 * future pharmacy-only features). Replaces the legacy `med-mover-feature`
 * `_preference` row in v2 instances.
 */
export const PharmacySalesSchema = z.object({
	enabled: z
		.boolean()
		.default(false)
		.describe(
			'When ON, the tenant sells to pharmacy clients and the CRM exposes pharmacy-specific UI (NPI / NCPDP fields on the client editor, pharmacy labeling, etc.). Independent of licensing.pharmacy_compliance — a tenant can sell to pharmacies without subscribing to the license-monitoring subsystem.',
		),
});

export const UrlSettingsSchema = z.object({
	crm: z.string().describe('The URL of the CRM application'),
});

/**
 * Shared schema for PDF document settings (order and quote)
 *
 * `title` metadata is the human label the config-web SettingFormDialog
 * renders for each field (falling back to the raw field name when
 * absent). `.meta({ title, description })` replaces `.describe(...)` —
 * describe is just sugar for `.meta({ description })` in Zod 4, so the
 * description behavior is unchanged.
 */
export const PdfSettingsSchema = z.object({
	senderName: z.string().nullable().optional().meta({
		title: 'Sender Name',
		description:
			'Override sender name (default: theme company name). Set null to omit.',
	}),
	senderEmail: z.string().nullable().optional().meta({
		title: 'Sender Email',
		description:
			'Override sender email (default: theme company email). Set null to omit.',
	}),
	senderAddressLines: z.array(z.string()).optional().meta({
		title: 'Sender Address Lines',
		description: 'Address lines shown under sender name',
	}),

	headerMessage: z.string().optional().meta({
		title: 'Header Message',
		description: 'HTML message above line items',
	}),
	footerMessage: z.string().optional().meta({
		title: 'Footer Message',
		description: 'HTML message below the summary',
	}),

	showSender: z.boolean().default(true).meta({
		title: 'Show Sender',
		description: 'Show the Sender section',
	}),
	showDiscount: z.boolean().default(true).meta({
		title: 'Show Discount',
		description: 'Show the Discount row',
	}),
	showShipping: z.boolean().default(true).meta({
		title: 'Show Shipping',
		description: 'Show the Shipping row',
	}),
});

/**
 * Invoice PDF extends with invoice-specific toggles
 */
export const InvoicePdfSettingsSchema = PdfSettingsSchema.extend({
	showDetailsBox: z.boolean().default(true).meta({
		title: 'Show Details Box',
		description: 'Show the Details box (PO#, terms, due date, credit)',
	}),
});

/**
 * Schema for the "Powered by X" attribution footer rendered across CRM
 * web, mobile (Capacitor), and email PDFs.
 *
 * 2026-05-23 — added so tenants can decide whether and how to attribute
 * the underlying platform. Default-on per Workd policy: every tenant
 * shows "Powered by Workd" unless they explicitly opt out, so the
 * platform gets visible credit across all the customer-facing surfaces.
 *
 * Per-storefront override lives in `PortalSiteSettings.brandAttribution`
 * (deferred to a follow-on pass — see `docs/claude/motor-city-defects/
 * branding-plan.md` for the storefront-override design).
 */
/**
 * Schema for the "Sanitize Workd users" tenant setting.
 *
 * When `enabled` is true (default for customer tenants), backend
 * user-LIST surfaces (Drivers list, Lead Eligibility, Activity Logs,
 * Login History) filter out Workd / Developrise staff for non-Workd
 * callers. The intent is to prevent a tenant admin from accidentally
 * picking a Workd staffer for production work when we happen to be
 * inside their tenant for testing/config.
 *
 * When `enabled` is false (set on internal tenants like `workd-sales`
 * where Workd staff ARE the real users), no sanitization happens —
 * Workd users are visible everywhere because they're the actual
 * operational identities.
 *
 * Default `true` is the conservative choice: a new customer tenant
 * gets sanitized automatically; an internal tenant has to opt out
 * explicitly. Only applies to user-LIST surfaces — assigned-entity
 * surfaces (route default driver, trip driver, client salesperson,
 * creator / modifier audit) always show the truth regardless of this
 * setting. See `libs/shared-server/src/utils/workd-user-filter.ts`
 * for the helper and CLAUDE.md "Workd User Visibility" for policy.
 */
export const SanitizeWorkdUsersSchema = z.object({
	enabled: z
		.boolean()
		.default(true)
		.describe(
			'When ON (default), filter Workd/Developrise staff out of user-list surfaces (Drivers list, Lead Eligibility, Activity Logs, Login History) for non-Workd callers. Turn OFF for internal tenants (workd-sales etc.) where Workd staff are the real operational users.',
		),
});

/**
 * Schema for the tenant's Twilio subaccount binding (PD-14218).
 *
 * `subAccountSid` is the Twilio subaccount (`AC…`) that owns this
 * tenant's softphone numbers. Hydrated onto TenantContext by the
 * unified-auth flow and read via `ContextService.getTwilioSubAccountSid()`
 * so authenticated rep call-control ops (coaching, recording control) and
 * phone-context-less paths (E911 address registration) re-root their Twilio
 * REST resource navigation to the subaccount instead of the master account.
 * Server-only (no `isPublic`). Empty string = tenant not yet migrated to a
 * subaccount → falls back to the Twilio master account (null-safe,
 * behavior unchanged pre-cutover).
 */
export const TwilioSubAccountSidSchema = z.object({
	subAccountSid: z
		.string()
		.default('')
		.describe(
			'Twilio subaccount SID (AC…) that owns this tenant’s softphone numbers. Set during the subaccount migration. Empty = not migrated; the runtime falls back to the Twilio master account.',
		),
});

/**
 * Schema for the master Twilio softphone on/off toggle (PD-14243).
 *
 * The explicit kill-switch the Workd team flips to enable/disable the human
 * Twilio softphone for a tenant WITHOUT deleting credentials. Only ONE input
 * to the derived gate (`getTwilioFeatureEnabled`): the softphone is truly "on"
 * only when this is true AND the tenant has a provisioned subaccount SID +
 * voice key (SSM) + ≥1 Kari's Law recipient. Written by the config-app
 * Enable-Phone flow (turns it on as part of provisioning) and the crm-api
 * kill-switch endpoint (flips it off in an emergency) — both write this same
 * key, so there is no drift. Server-only.
 */
export const TwilioFeatureEnabledSchema = z.object({
	enabled: z
		.boolean()
		.default(false)
		.describe(
			'Master on/off for the tenant human softphone. Off by default; only one input to the derived "on" gate (which also needs a subaccount + voice key + Kari\'s Law recipients).',
		),
});

/**
 * Schema for the Item Intelligence engine tuning knobs.
 *
 * Drives the nightly "Frequently Purchased Together" association
 * calculation (item-purchased-together queue handler). The defaults
 * match the values that were previously hardcoded in the handler, so
 * tenants without a setting row keep the historical behavior. A
 * knowledgeable admin can tune these per tenant from Settings → Item
 * Intelligence; changes take effect on the next nightly recalculation.
 */
export const ItemIntelligenceSchema = z.object({
	lookbackMonths: z
		.number()
		.int('Must be a whole number')
		.min(1, 'Minimum is 1 month')
		.max(60, 'Maximum is 60 months')
		.default(12)
		.describe(
			'How many months of invoice history to analyze when computing purchased-together associations',
		),
	topN: z
		.number()
		.int('Must be a whole number')
		.min(1, 'Minimum is 1')
		.max(100, 'Maximum is 100')
		.default(25)
		.describe(
			'Maximum number of associated items to keep per source item (ranked by cosine similarity)',
		),
	minCoOccurrence: z
		.number()
		.int('Must be a whole number')
		.min(1, 'Minimum is 1')
		.max(100, 'Maximum is 100')
		.default(3)
		.describe(
			'Minimum number of invoices two items must appear on together before the pair is considered at all',
		),
	recommendationTopN: z
		.number()
		.int('Must be a whole number')
		.min(1, 'Minimum is 1')
		.max(200, 'Maximum is 200')
		.default(25)
		.describe(
			'How many recommended items to precompute and store per account (the account recommender)',
		),
	recommendationMinScore: z
		.number()
		.min(0, 'Minimum is 0')
		.default(0)
		.describe(
			'Minimum affinity score for a recommendation to be kept. 0 keeps the top-N regardless; raise it to prune weak suggestions',
		),
	coldStartMinItems: z
		.number()
		.int('Must be a whole number')
		.min(0, 'Minimum is 0')
		.max(100, 'Maximum is 100')
		.default(3)
		.describe(
			'Below this many distinct purchased items, an account is treated as cold-start and gets popularity-based suggestions instead of personalized ones',
		),
	lookalikeNeighborCount: z
		.number()
		.int('Must be a whole number')
		.min(0, 'Minimum is 0')
		.max(25, 'Maximum is 25')
		.default(5)
		.describe(
			'How many nearest lookalike accounts (by purchase-vector similarity) feed the "Popular with accounts similar to this one" recommendation layer. 0 disables the layer',
		),
});

export const BrandAttributionSchema = z.object({
	show: z
		.boolean()
		.default(true)
		.describe(
			'Render the "Powered by X" footer in the app shell. When off, the footer is hidden everywhere (CRM web, mobile, email PDFs).',
		),
	label: z
		.string()
		.max(60, 'Label must be 60 characters or fewer')
		.nullable()
		.default(null)
		.describe(
			'The brand name shown after the fixed "Powered by " prefix (e.g. "Workd", "Ice Mover"). Leave blank for the brand default. A leading "Powered by " is stripped on render, so legacy values like "Powered by Acme" still display correctly.',
		),
	logoUrl: z
		.string()
		.url('Must be a valid URL')
		.nullable()
		.default(null)
		.describe(
			'Optional logo image URL shown in place of the brand name after "Powered by " (e.g. the Workd wordmark). When set, the logo replaces the text label. Leave blank to show the text label instead.',
		),
	logoUrlDark: z
		.string()
		.url('Must be a valid URL')
		.nullable()
		.default(null)
		.describe(
			'Optional dark-mode logo image URL, used in place of logoUrl when the app is in dark mode (e.g. a white wordmark). Falls back to logoUrl when not set.',
		),
	linkUrl: z
		.string()
		.url('Must be a valid URL')
		.nullable()
		.default('https://workd.com')
		.describe(
			'Optional click-through URL for the attribution. Leave blank to render plain text with no link.',
		),
});

/**
 * Registry mapping setting keys to their schemas and metadata
 *
 * Key format: Technical identifier stored in database
 *   - ACCESS_LOGGING (upper snake case, like env var)
 *   - conversation_agents.monthly_credits (dot notation for namespacing)
 *
 * Label format: Human-readable display name (in registry, NOT in database)
 *   - "Access Logging" - shown in UI
 *   - "AI Agent Credits" - shown in UI
 */
/**
 * Statuses a tenant may pick as the post-submission default. Deliberately a
 * SUBSET of OrderStatus — excludes transient/system statuses (unsubmitted,
 * quote, cancelled, override*) that make no sense as a submission landing
 * state. PD-14226.
 */
export const DEFAULT_SUBMISSION_STATUS_OPTIONS: ReadonlyArray<OrderStatus> = [
	OrderStatus.PENDING,
	OrderStatus.APPROVED,
	OrderStatus.APPROVAL_REQUIRED,
	OrderStatus.SHIPPED,
];

/**
 * Schema for the per-tenant default order-submission status, configurable
 * separately for CRM and ecommerce. This is the status an order lands in on
 * submission UNLESS the order-submission checks force Approval Required
 * (those always win — see orders.service.submitOrder /
 * portal-cart-submission.service.submitCart). Setting ecommerce to Approval
 * Required is how a tenant auto-holds every storefront order before it
 * reaches the ERP.
 */
export const DefaultSubmissionStatusSchema = z.object({
	crm: z
		.nativeEnum(OrderStatus)
		.refine((s) => DEFAULT_SUBMISSION_STATUS_OPTIONS.includes(s), {
			message: 'Not an allowed default submission status',
		})
		.default(OrderStatus.PENDING),
	ecommerce: z
		.nativeEnum(OrderStatus)
		.refine((s) => DEFAULT_SUBMISSION_STATUS_OPTIONS.includes(s), {
			message: 'Not an allowed default submission status',
		})
		.default(OrderStatus.PENDING),
	// PD-14234: roles (rbac_role ids) additionally allowed to approve/reject
	// orders held ONLY because the channel default status is Approval
	// Required (the DEFAULT_SETTING approval reason). Users holding the
	// blanket orders:approve / orders:reject actions are always allowed and
	// don't need to be listed here.
	crmApproverRoleIds: z.array(z.number()).optional(),
	ecommerceApproverRoleIds: z.array(z.number()).optional(),
});

/**
 * Schema for the per-tenant default State on new clients (PD-14340). When set
 * (a US state code/name), the New Client form pre-fills the State field; empty
 * means no default. Most tenants leave it empty; it's for customers who only
 * service one state.
 */
export const ClientDefaultStateSchema = z.object({
	state: z
		.string()
		.default('')
		.describe('Default State applied to new clients (empty = no default)'),
});

// PD-14335 — tenant default credit limit. Stored as an object (the dominant
// registry convention) with a single nullable `amount`. Null = no default.
export const DefaultCreditLimitSchema = z.object({
	amount: z.number().min(0, 'Cannot be negative').nullable().default(null),
});

export type DefaultCreditLimitValue = z.infer<typeof DefaultCreditLimitSchema>;

/**
 * Schema for per-entity record mastering (PD-14372). For each lockable entity
 * (client/item/vendor/license, and any future entity in LOCKABLE_ENTITIES), a
 * tenant may set which system is master and override the locked-field lists.
 * A loose `z.record(string, …)` keeps it scalable — adding a new lockable
 * entity is a registry change, not a schema change. Absent keys fall back to
 * the code registry defaults (external master + historical hardcoded fields),
 * so the default `{}` reproduces today's behavior for every tenant.
 */
export const RecordMasterEnumSchema = z.enum(['external', 'workd']);
export const EntityMasteringConfigSchema = z.object({
	master: RecordMasterEnumSchema.optional().describe(
		'external (default): external system owns the record, its fields are locked when it has an externalId. workd: Workd owns the record, it stays editable.',
	),
	lockedFields: z
		.array(z.string())
		.optional()
		.describe(
			'Override of which standard fields are locked when external is master. Absent = the entity default list.',
		),
	lockedCustomFields: z
		.array(z.number())
		.optional()
		.describe('Custom-field ids locked when external is master.'),
});
export const RecordMasteringSchema = z
	.record(z.string(), EntityMasteringConfigSchema)
	.default({});

export const INSTANCE_SETTINGS_REGISTRY = {
	ACCESS_LOGGING: {
		label: 'Access Logging',
		description: 'Configure access log verbosity for debugging',
		category: 'System',
		schema: AccessLoggingSchema,
		defaultValue: { verboseLogging: false },
	},
	'branding.attribution': {
		label: 'Brand Attribution Footer',
		description:
			'Controls the "Powered by X" footer rendered across CRM web, mobile, and email PDFs. Default shows "Powered by Workd". White-label tenants can override the label or hide the footer entirely.',
		category: 'Branding',
		schema: BrandAttributionSchema,
		// `isPublic: true` allows this value to flow into the
		// pre-auth public-bootstrap response so the login screen
		// can render the attribution before a user is identified.
		// Other settings (RingCentral creds, AI throttle, etc.)
		// stay server-only by default — no `isPublic` flag, no
		// exposure.
		isPublic: true,
		defaultValue: {
			show: true,
			label: null,
			logoUrl: null,
			logoUrlDark: null,
			linkUrl: 'https://workd.com',
		},
	},
	'conversation_agents.monthly_credits': {
		label: 'AI Agent Credits',
		description: 'Monthly voice minute and LLM token limits',
		category: 'AI Agents',
		schema: ConversationAgentCreditsSchema,
		defaultValue: {
			voiceMinuteCredits: 100,
			llmTokenCredits: 1_000_000,
		},
	},
	'ai_agents.system_agent_enabled': {
		label: 'System AI Agent Enabled',
		description:
			'Enable the system AI agent (Sales Helper) chat widget for this tenant',
		category: 'AI Agents',
		schema: SystemAgentEnabledSchema,
		defaultValue: { enabled: false },
	},
	'ringcentral.settings': {
		label: 'RingCentral Settings',
		description: 'Configuration for RingCentral click-to-dial integration',
		category: 'Integrations',
		schema: RingCentralSettingsSchema,
		defaultValue: { callMode: 'ringout' },
	},
	'throttle.concurrency': {
		label: 'AI Agent Throttle Concurrency',
		description: 'Max concurrent AI agent sessions per channel type',
		category: 'AI Agents',
		schema: ThrottleConcurrencySchema,
		defaultValue: {
			inbound: 10,
			outbound: 10,
			chat: 10,
		},
	},
	URL: {
		label: 'URLs',
		description: 'URLs related to this instance',
		category: 'System',
		schema: UrlSettingsSchema,
	},
	'pdf.invoice': {
		label: 'Invoice PDF',
		description:
			'Customize invoice PDF layout, sender info, and section visibility',
		category: 'Documents',
		schema: InvoicePdfSettingsSchema,
		defaultValue: {
			showSender: true,
			showDetailsBox: true,
			showDiscount: true,
			showShipping: true,
		},
	},
	'pdf.order': {
		label: 'Order PDF',
		description:
			'Customize order PDF layout, sender info, and section visibility',
		category: 'Documents',
		schema: PdfSettingsSchema,
		defaultValue: {
			showSender: true,
			showDiscount: true,
			showShipping: true,
		},
	},
	'pdf.quote': {
		label: 'Quote PDF',
		description:
			'Customize quote PDF layout, sender info, and section visibility',
		category: 'Documents',
		schema: PdfSettingsSchema,
		defaultValue: {
			showSender: true,
			showDiscount: true,
			showShipping: true,
		},
	},
	'erp.integration': {
		label: 'ERP Integration',
		description:
			"Whether Workd serves as this tenant's ERP or integrates with a third-party ERP. When Workd IS the ERP, orders remain cancellable after approval. When integrated with a third-party ERP, orders become non-cancellable once approved (because they've been pushed downstream).",
		category: 'Integrations',
		schema: ErpIntegrationSchema,
		defaultValue: { mode: 'workd_as_erp' },
	},
	'licensing.pharmacy_compliance': {
		label: 'Pharmacy License Compliance',
		description:
			'Enable pharmacy-specific compliance features including NPI, DEA verification, state board registry sync, and enhanced order blocking',
		category: 'Licensing',
		schema: PharmacyComplianceSchema,
		defaultValue: { enabled: false },
	},
	'features.pharmacy_sales': {
		label: 'Sells to Pharmacies',
		description:
			'Mark this tenant as one that sells to pharmacy clients. When ON, the CRM shows pharmacy-specific UI (NPI / NCPDP fields on the client editor, pharmacy labeling, future pharmacy-only features). Independent of the Pharmacy License Compliance setting.',
		category: 'Features',
		schema: PharmacySalesSchema,
		defaultValue: { enabled: false },
	},
	'items.intelligence': {
		label: 'Item Intelligence',
		description:
			'Tuning for the "Frequently Purchased Together" association engine: how many months of invoice history to analyze, how many associations to keep per item, and the minimum co-purchase count for a pair to qualify. Defaults match the built-in engine behavior.',
		category: 'Items',
		schema: ItemIntelligenceSchema,
		defaultValue: {
			lookbackMonths: 12,
			topN: 25,
			minCoOccurrence: 3,
			recommendationTopN: 25,
			recommendationMinScore: 0,
			coldStartMinItems: 3,
			lookalikeNeighborCount: 5,
		},
	},
	'notifications.lead_registration_email': {
		label: 'Lead Registration Notification Email',
		description:
			'Email address (or comma-separated list) that receives a notification when a new self-serve customer lead is submitted via /api/portal/register. Per-portal_site overrides win over this tenant-level default.',
		category: 'Notifications',
		schema: LeadRegistrationEmailSchema,
		defaultValue: '',
	},
	'ordering.default_submission_status': {
		label: 'Default Order Status on Submission',
		description:
			'The status an order transitions to when submitted, set separately for CRM and ecommerce. Order-submission checks (credit hold, credit limit, etc.) still override this to Approval Required when they trigger — this only sets the default for an otherwise-clean order. Set ecommerce to Approval Required to auto-hold all storefront orders before they reach the ERP.',
		category: 'Ordering',
		schema: DefaultSubmissionStatusSchema,
		defaultValue: {
			crm: OrderStatus.PENDING,
			ecommerce: OrderStatus.PENDING,
		},
	},
	'clients.default_state': {
		label: 'Default State for New Clients',
		description:
			'When set, the New Client form pre-fills the State field with this value (still editable). Empty = no default. Useful for tenants that only service accounts in one state.',
		category: 'Clients',
		schema: ClientDefaultStateSchema,
		defaultValue: { state: '' },
	},
	'finance.default_credit_limit': {
		label: 'Default Credit Limit',
		description:
			"Default credit limit prefilled into a new client's Credit Limit on the create/edit form when it is empty. Skipped for ERP-integrated clients (those with an externalId). Empty = no default.",
		category: 'Ordering',
		schema: DefaultCreditLimitSchema,
		defaultValue: { amount: null },
	},
	'users.sanitize_workd_users': {
		label: 'Sanitize Workd Users in Selectors',
		description:
			"When ON (default), Workd/Developrise staff are filtered out of user-list surfaces (Drivers list, Lead Eligibility, Activity Logs, Login History) for non-Workd callers — prevents a tenant admin from accidentally picking us. Turn OFF for internal tenants like workd-sales where Workd staff are the actual operational users. Doesn't affect assigned-entity surfaces (route default driver, trip driver, client salesperson, creator/modifier) — those always show the truth.",
		category: 'Users',
		schema: SanitizeWorkdUsersSchema,
		defaultValue: { enabled: true },
	},
	'softphone.twilioSubAccountSid': {
		label: 'Twilio Subaccount SID',
		description:
			'The Twilio subaccount (AC…) that owns this tenant’s softphone numbers. Set during the subaccount migration so the runtime scopes call-control, recording, coaching, and E911 REST operations to the tenant’s own subaccount (isolation). Empty/unset = the tenant still runs on the Twilio master account.',
		category: 'Integrations',
		schema: TwilioSubAccountSidSchema,
		defaultValue: { subAccountSid: '' },
	},
	'softphone.twilioFeatureEnabled': {
		label: 'Twilio Softphone Enabled',
		description:
			'Master on/off kill-switch for the tenant human Twilio softphone (PD-14243). Off by default. Only one input to the derived "on" gate — the softphone is truly on only when this is true AND a subaccount SID + voice key are provisioned AND ≥1 Kari’s Law recipient is configured. Disabling does NOT delete credentials; re-enabling restores. Written by the config-app Enable-Phone flow and the crm-api kill-switch endpoint (same key, no drift).',
		category: 'Integrations',
		schema: TwilioFeatureEnabledSchema,
		defaultValue: { enabled: false },
	},
	'integrations.record_mastering': {
		label: 'Record Mastering',
		description:
			"Per-entity source of truth for externally-linked records. For each entity (Client, Item, Vendor, License) choose whether the external system is master (default — external-id records are locked read-only in the CRM) or Workd is master (records stay editable and sync out). Also overrides which standard/custom fields are locked. Developer/admin-facing — mis-setting this only removes the edit lock; it does NOT change sync direction, so set 'Workd' only for entities whose integration pushes changes OUT. Default (empty) = today's behavior for every tenant.",
		category: 'Integrations',
		schema: RecordMasteringSchema,
		defaultValue: {},
	},
} as const;

/**
 * Infer TypeScript types from schemas
 */
export type AccessLoggingValue = z.infer<typeof AccessLoggingSchema>;
export type SystemAgentEnabledValue = z.infer<typeof SystemAgentEnabledSchema>;
export type ConversationAgentCreditsValue = z.infer<
	typeof ConversationAgentCreditsSchema
>;
export type ThrottleConcurrencyValue = z.infer<
	typeof ThrottleConcurrencySchema
>;
export type RingCentralSettingsValue = z.infer<
	typeof RingCentralSettingsSchema
>;
export type PdfSettingsValue = z.infer<typeof PdfSettingsSchema>;
export type InvoicePdfSettingsValue = z.infer<typeof InvoicePdfSettingsSchema>;
export type PharmacyComplianceValue = z.infer<typeof PharmacyComplianceSchema>;
export type ErpIntegrationValue = z.infer<typeof ErpIntegrationSchema>;
export type PharmacySalesValue = z.infer<typeof PharmacySalesSchema>;
export type BrandAttributionValue = z.infer<typeof BrandAttributionSchema>;
export type ItemIntelligenceValue = z.infer<typeof ItemIntelligenceSchema>;
export type SanitizeWorkdUsersValue = z.infer<typeof SanitizeWorkdUsersSchema>;
export type TwilioSubAccountSidValue = z.infer<
	typeof TwilioSubAccountSidSchema
>;
export type DefaultSubmissionStatusValue = z.infer<
	typeof DefaultSubmissionStatusSchema
>;
export type ClientDefaultStateValue = z.infer<typeof ClientDefaultStateSchema>;
export type RecordMasteringValue = z.infer<typeof RecordMasteringSchema>;

/**
 * Generic type helper for type-safe setting access
 */
export type InstanceSettingValue<
	K extends keyof typeof INSTANCE_SETTINGS_REGISTRY,
> = z.infer<(typeof INSTANCE_SETTINGS_REGISTRY)[K]['schema']>;

/**
 * Helper to get all setting keys (for UI dropdown)
 */
export type InstanceSettingKey = keyof typeof INSTANCE_SETTINGS_REGISTRY;

/**
 * Setting keys that are safe to expose to unauthenticated / pre-login
 * frontends (login screen, public storefront landing). Computed from the
 * `isPublic: true` flag in the registry so adding a new public setting
 * is one line in the registry, not two places.
 *
 * Server endpoints that surface public settings (the bootstrap response
 * served to anonymous visitors) MUST filter against this list. Anything
 * not present here is server-only.
 */
export const PUBLIC_INSTANCE_SETTING_KEYS: ReadonlyArray<InstanceSettingKey> =
	Object.entries(INSTANCE_SETTINGS_REGISTRY)
		.filter(([, cfg]) => (cfg as { isPublic?: boolean }).isPublic === true)
		.map(([key]) => key as InstanceSettingKey);
